import { useTranslation } from 'react-i18next';
import { SellerRoutesEnum } from '../../core/enums';
import { normalizeDateFE } from '../../core/normalization';
import AgendaModal from '../AgendaModal/AgendaModal';
import PaymentInstructionModal from '../PaymentInstructionModal/PaymentInstructionModal';
import useAgendaModal from '../../pages/Seller/SellerFiling/useAgendaModal';
import usePaymentInstructionModal from '../../pages/Seller/SellerFiling/usePaymentInstructionModal';
import { SellerFilingReportToConfirm } from '../../core/types';
import { differenceInCalendarMonths, subMonths } from 'date-fns';
import { formatDate, formatNumber, getUTCDate } from '../../core/helpers';
import OpenTask, { TodoItem } from './OpenTask';
import { useLayoutEffect, useRef, useState } from 'react';
import Button from '../Button';
import './OpenTasks.scss';
import { v4 as uuidv4 } from 'uuid';
import GreenAlert from '../GreenAlert/GreenAlert';

interface Props {
  manualUploadNeeded: boolean | undefined;
  purchasePricesNeeded: boolean | undefined;
  reportsToConfirmPayment: SellerFilingReportToConfirm[];
  reportsToConfirmAgenda: SellerFilingReportToConfirm[];
}

export default function OpenTasks({
  manualUploadNeeded,
  purchasePricesNeeded,
  reportsToConfirmPayment,
  reportsToConfirmAgenda,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState(0);

  const { t } = useTranslation();
  const [isExtended, setIsExtended] = useState(false);

  const {
    onClickConfirmPayment,
    isPaymentInstructionModalOpened,
    closePaymentInstructionModal,
    reportToConfirmPayment,
    handleOpenPaymentInstructionModal,
    isConfirmingPayment,
  } = usePaymentInstructionModal();

  const {
    isAgendaModalOpened,
    closeAgendaModal,
    handleOpenAgendaModal,
    onClickConfirmAgenda,
    isConfirmingAgenda,
    reportToConfirmAgenda,
  } = useAgendaModal();

  const todos = generateTodos(
    t,
    handleOpenAgendaModal,
    handleOpenPaymentInstructionModal
  )({
    manualUpload: manualUploadNeeded,
    purchasePrices: purchasePricesNeeded,
    reportsToConfirmPayment,
    reportsToConfirmAgenda,
  });

  useLayoutEffect(() => {
    if (ref.current) {
      setMaxHeight(isExtended ? ref.current.offsetHeight : 0);
    }
  }, [isExtended]);

  if (!todos.length) return null;

  const ongoing = todos.filter(
    (todo) => differenceInCalendarMonths(getUTCDate(), todo.date) <= 0
  );
  const expired = todos
    .filter((todo) => differenceInCalendarMonths(getUTCDate(), todo.date) > 0)
    .sort((a, b) => {
      return a.date.getTime() - b.date.getTime();
    });

  return (
    <div className='OpenTasks mb-3'>
      {!!ongoing.length ? (
        <div className='mb-3'>
          <div className='fw-800 mb-2' data-testid='ongoing-tasks-header'>
            {t('Ongoing tasks')}&nbsp;{ongoing.length}
          </div>
          {ongoing
            .sort((a, b) => {
              return a.date.getTime() - b.date.getTime();
            })
            .map((item, i) => (
              <OpenTask
                item={item}
                i={i}
                key={uuidv4()}
                id={item.id}
                type='ongoing'
              />
            ))}
        </div>
      ) : (
        <GreenAlert />
      )}
      {!!expired.length && (
        <>
          <div className='mb-3'>
            <div className='fw-800 mb-2' data-testid='expired-tasks-header'>
              {t('Expired tasks')}&nbsp;{expired.length}
            </div>
            <div>
              {expired.slice(0, 3).map((item, i) => (
                <OpenTask
                  item={item}
                  i={i}
                  key={uuidv4()}
                  id={item.id}
                  type='expired'
                />
              ))}
              <div
                className='OpenTasks__collapse'
                style={{
                  maxHeight,
                }}
                data-testid='expired-tasks-collapse'
              >
                <div ref={ref}>
                  {expired.slice(3).map((item, i) => (
                    <OpenTask
                      item={item}
                      i={i + 3}
                      key={uuidv4()}
                      id={item.id}
                      type='expired'
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
          {expired.length > 3 && (
            <Button
              data-testid='expired-tasks-btn'
              className='w-100 mb-2'
              styling='gray'
              onClick={() => setIsExtended((s) => !s)}
            >
              {isExtended
                ? t('Show less')
                : t('Show *count* more').replace(
                    '*count*',
                    String(expired.slice(3).length)
                  )}
            </Button>
          )}
        </>
      )}
      <AgendaModal
        report={reportToConfirmAgenda}
        isModalOpened={isAgendaModalOpened}
        closeModal={closeAgendaModal}
        onClickConfirm={onClickConfirmAgenda(reportToConfirmAgenda)}
        isConfirming={isConfirmingAgenda}
      />
      <PaymentInstructionModal
        report={reportToConfirmPayment}
        isModalOpened={isPaymentInstructionModalOpened}
        closeModal={closePaymentInstructionModal}
        onClickConfirm={onClickConfirmPayment(reportToConfirmPayment)}
        isConfirming={isConfirmingPayment}
      />
    </div>
  );
}

const generateTodos =
  (
    t: (s: string) => string,
    handleOpenAgendaModal: (r: SellerFilingReportToConfirm) => () => void,
    handleOpenPaymentInstructionModal: (
      r: SellerFilingReportToConfirm
    ) => () => void
  ) =>
  ({
    manualUpload,
    purchasePrices,
    reportsToConfirmPayment,
    reportsToConfirmAgenda,
  }: {
    manualUpload: boolean | undefined;
    purchasePrices: boolean | undefined;
    reportsToConfirmPayment: SellerFilingReportToConfirm[];
    reportsToConfirmAgenda: SellerFilingReportToConfirm[];
  }): TodoItem[] => {
    const date = new Date();
    date.setDate(10);

    return [
      ...(manualUpload
        ? [
            {
              label: t('Upload manual turnover data'),
              description: t('Upload manual turnover data for *month*').replace(
                '*month*',
                formatDate(subMonths(date, 1), 'MMMM yyyy')
              ),
              date,
              action: SellerRoutesEnum.DataFiles,
              btnLabel: 'Upload',
              id: 'manual-upload',
            },
          ]
        : []),
      ...(purchasePrices
        ? [
            {
              label: t('Update purchase prices'),
              description: t('Upload purchase prices for *month*').replace(
                '*month*',
                formatDate(subMonths(date, 1), 'MMMM yyyy')
              ),
              date,
              action: SellerRoutesEnum.PurchasePrices,
              btnLabel: 'Update',
              id: 'purchase-prices',
            },
          ]
        : []),

      ...reportsToConfirmPayment.map((r) => ({
        label: t('*amount* payment *country*')
          .replace(
            '*country*',
            r.type === 'oss' ? 'OSS' : t(r.country.short_name)
          )
          .replace(
            '*amount*',
            formatNumber(r.payment_instruction.amount.amount / 100, {
              currency: r.payment_instruction.amount.currency,
            })
          ),
        description: `${t('Period')}: ${formatDate(
          normalizeDateFE(r.period.from_date),
          'MMMM yyyy'
        )}`,
        date: normalizeDateFE(
          r.payment_instruction.due_date || r.deadline_date
        ),
        action: handleOpenPaymentInstructionModal(r),
        btnLabel: 'Open',
        id: `report-${r.id}-${r.payment_instruction.id}-payment`,
      })),
      ...reportsToConfirmAgenda.map((r) => ({
        label: t('Submit Germany report to Agenda'),
        description: t('Please confirm once it is done'),
        date: normalizeDateFE(
          r.payment_instruction?.due_date || r.deadline_date
        ),
        action: handleOpenAgendaModal(r),
        btnLabel: 'Open',
        id: `report-${r.id}-${r.payment_instruction.id}-agenda`,
      })),
    ];
  };
